import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { format } from 'date-fns';
import { of } from 'rxjs';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { PersonImportAPI } from '../api/PersonImportAPI.js';
import { PersonImportStrategyValidation } from '../entities/PersonImportStrategyValidation.js';

/**
 * The last three imports that can still be opened - one that was closed without running, or whose rows
 * were not all imported - so the work on a long file does not have to start over. Live, so a run or a
 * removal updates the list right away.
 *
 * @param enabled only a user who may import sees them - the subscription would be refused otherwise
 */
export function useRecentPersonImports(enabled: boolean): PersonImportStrategyValidation[] {
  const { value } = useQuery(
    () =>
      enabled
        ? PersonImportAPI.subscribeRecent()(selectionSet(() => PersonImportStrategyValidation, 1))
        : of([] as PersonImportStrategyValidation[]),
    [enabled],
  );
  return value ?? [];
}

/** how an import is named in a list: its file and when it was uploaded */
export const recentPersonImportLabel = (entry: PersonImportStrategyValidation) =>
  `${entry.fileName || 'Unbenannte Datei'} · ${format(entry.createdAt, 'dd.MM. HH:mm')}`;

/**
 * Throws an import away without running it. Not confirmed through a dialog: the list sits in a focus
 * dropdown, which closes the moment a dialog takes the focus - the caller asks with a second click instead.
 */
export async function removePersonImport(entry: PersonImportStrategyValidation): Promise<void> {
  await PersonImportAPI.removeProcess(entry.importProcessId ?? '')().catch((error: Error) =>
    SgSnackbar.showError(error.message),
  );
}
